import { useState } from "react";
import { Card, Table, Tag, Button, Modal, Form, Input, DatePicker, Select, Switch, message, Popconfirm, Space } from "antd";
import { useAnnouncements, useAnnouncementCreate, useAnnouncementUpdate, useAnnouncementDelete } from "../../../api-hooks/announcements";
import dayjs from "dayjs";

const TYPE_COLOR: Record<string, string> = { info: "blue", warning: "orange", maintenance: "red", feature: "green" };

export default function AnnouncementsPage() {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<any>(null);
  const [messageApi, ctx] = message.useMessage();

  const { data: rows = [], isLoading } = useAnnouncements();
  const create = useAnnouncementCreate({ onSuccess: () => { messageApi.success("Created"); setEditing(null); }, onError: () => messageApi.error("Failed") });
  const update = useAnnouncementUpdate({ onSuccess: () => { messageApi.success("Saved"); setEditing(null); }, onError: () => messageApi.error("Failed") });
  const del = useAnnouncementDelete({ onSuccess: () => messageApi.success("Deleted"), onError: () => messageApi.error("Failed") });

  const openEdit = (row: any) => {
    setEditing(row);
    form.setFieldsValue({
      ...row,
      show_from: row.show_from ? dayjs(row.show_from) : null,
      show_until: row.show_until ? dayjs(row.show_until) : null,
    });
  };

  const openCreate = () => { setEditing({}); form.resetFields(); };

  const handleSave = (vals: any) => {
    const body = {
      ...vals,
      show_from: vals.show_from?.toISOString(),
      show_until: vals.show_until?.toISOString(),
    };
    if (editing?.id) update.mutate({ id: editing.id, data: body });
    else create.mutate(body);
  };

  const columns = [
    { title: "Title", dataIndex: "title", ellipsis: true, render: (v: string) => <span className="font-medium">{v}</span> },
    { title: "Type", dataIndex: "type", render: (v: string) => <Tag color={TYPE_COLOR[v] || "blue"}>{v}</Tag> },
    { title: "Target", dataIndex: "target", render: (v: string) => v.replace(/_/g, " ") },
    { title: "Show From", dataIndex: "show_from", render: (v: string) => v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—" },
    { title: "Show Until", dataIndex: "show_until", render: (v: string) => v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—" },
    { title: "Active", dataIndex: "is_active", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "Actions", key: "actions", render: (_: any, row: any) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(row)}>Edit</Button>
          <Popconfirm title="Delete announcement?" onConfirm={() => del.mutate(row.id)}>
            <Button size="small" danger type="text">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">System Announcements</h2>
          <p className="text-slate-500 text-sm mt-0.5">Platform-wide banners shown to all companies</p>
        </div>
        <Button type="primary" onClick={openCreate}>New Announcement</Button>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <Table dataSource={rows} columns={columns} rowKey="id" loading={isLoading} size="small" pagination={false} />
      </Card>

      <Modal title={editing?.id ? "Edit Announcement" : "New Announcement"} open={!!editing} onCancel={() => setEditing(null)}
        onOk={() => form.validateFields().then(handleSave)} okText="Save"
        confirmLoading={create.isPending || update.isPending} width={600}>
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label="Body" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="type" label="Type" initialValue="info">
              <Select options={["info","warning","maintenance","feature"].map(t => ({ value: t, label: t }))} />
            </Form.Item>
            <Form.Item name="target" label="Target" initialValue="all">
              <Select options={["all","plan_specific","specific_companies"].map(t => ({ value: t, label: t.replace(/_/g, " ") }))} />
            </Form.Item>
            <Form.Item name="show_from" label="Show From" rules={[{ required: true }]}>
              <DatePicker showTime style={{ width: "100%" }} format="DD/MM/YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="show_until" label="Show Until" rules={[{ required: true }]}>
              <DatePicker showTime style={{ width: "100%" }} format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </div>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
